import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Flag, Users, Activity, FlaskConical } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface LiveClass {
  id: string;
  grade: number;
  subject: string | null;
  title: string;
  description: string | null;
  teacherName: string;
  scheduledAt: string;
  durationMinutes: number;
  meetingUrl: string | null;
  topicId: string | null;
  quizQuestionCount: number | null;
  status: "upcoming" | "live" | "ended" | "cancelled";
  recordingUrl: string | null;
  createdAt: string;
  endedAt: string | null;
}

interface FeatureStatus {
  compiledIn: boolean;
  runtime: boolean;
  live: boolean;
  defaultQuizLength: number;
}

const EMPTY_CLASS: Omit<LiveClass, "id" | "status" | "createdAt" | "endedAt"> = {
  grade: 4,
  subject: "",
  title: "",
  description: "",
  teacherName: "",
  scheduledAt: "",
  durationMinutes: 60,
  meetingUrl: "",
  topicId: null,
  quizQuestionCount: null,
  recordingUrl: "",
};

function formatLocal(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", { timeZone: "America/Jamaica", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
}

export default function AdminLiveClasses() {
  const { toast } = useToast();
  const [editing, setEditing] = useState<LiveClass | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<typeof EMPTY_CLASS>(EMPTY_CLASS);
  const [gradeFilter, setGradeFilter] = useState<string>("all");

  const { data: status, isLoading: statusLoading } = useQuery<FeatureStatus>({
    queryKey: ["/api/admin/summer-classes/status"],
    queryFn: async () => (await apiRequest("GET", "/api/admin/summer-classes/status")).json(),
  });

  const { data: classes = [], isLoading: classesLoading } = useQuery<LiveClass[]>({
    queryKey: ["/api/admin/live-classes", gradeFilter],
    queryFn: async () => (await apiRequest("GET", `/api/admin/live-classes${gradeFilter !== "all" ? `?grade=${gradeFilter}` : ""}`)).json(),
    enabled: !!status?.compiledIn,
  });

  const setRuntimeFlag = useMutation({
    mutationFn: async (enabled: boolean) =>
      (await apiRequest("PUT", "/api/admin/settings/summer_classes_enabled", { value: enabled ? "true" : "false" })).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/summer-classes/status"] });
      toast({ title: "Saved" });
    },
    onError: (e: any) => toast({ title: "Failed to save", description: e?.message, variant: "destructive" }),
  });

  const setDefaultQuizLength = useMutation({
    mutationFn: async (n: number) =>
      (await apiRequest("PUT", "/api/admin/settings/summer_classes_default_quiz_length", { value: String(n) })).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/summer-classes/status"] });
      toast({ title: "Default quiz length saved" });
    },
  });

  const createClass = useMutation({
    mutationFn: async (payload: typeof EMPTY_CLASS) => (await apiRequest("POST", "/api/admin/live-classes", payload)).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/live-classes"] });
      setCreating(false);
      setForm(EMPTY_CLASS);
      toast({ title: "Class created" });
    },
    onError: (e: any) => toast({ title: "Create failed", description: e?.message, variant: "destructive" }),
  });

  const updateClass = useMutation({
    mutationFn: async (vars: { id: string; payload: any }) => (await apiRequest("PATCH", `/api/admin/live-classes/${vars.id}`, vars.payload)).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/live-classes"] });
      setEditing(null);
      toast({ title: "Class updated" });
    },
    onError: (e: any) => toast({ title: "Update failed", description: e?.message, variant: "destructive" }),
  });

  const deleteClass = useMutation({
    mutationFn: async (id: string) => (await apiRequest("DELETE", `/api/admin/live-classes/${id}`)).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/live-classes"] });
      toast({ title: "Class deleted" });
    },
  });

  const endClass = useMutation({
    mutationFn: async (id: string) => (await apiRequest("POST", `/api/admin/live-classes/${id}/end`, {})).json(),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/live-classes"] });
      toast({ title: "Class ended", description: `Quizzes assigned: ${data.quizzesAssigned ?? 0}` });
    },
    onError: (e: any) => toast({ title: "End class failed", description: e?.message, variant: "destructive" }),
  });

  const seedDemo = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/admin/live-classes/seed-demo", {})).json(),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/live-classes"] });
      toast({ title: "Demo classes seeded", description: `${data.created ?? 0} classes created across Grades 4/5/6/7.` });
    },
    onError: (e: any) => toast({ title: "Seed failed", description: e?.message, variant: "destructive" }),
  });

  if (statusLoading) return <div className="p-6">Loading…</div>;

  // env-var gate not on → feature is fully disabled at the build level
  if (!status?.compiledIn) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Summer Live Classes</CardTitle>
          <CardDescription>Feature is not compiled into this build (SUMMER_CLASSES_ENABLED env var is false).</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const start = (cls: LiveClass) => {
    setEditing(cls);
    setForm({
      grade: cls.grade,
      subject: cls.subject ?? "",
      title: cls.title,
      description: cls.description ?? "",
      teacherName: cls.teacherName,
      scheduledAt: cls.scheduledAt ? new Date(cls.scheduledAt).toISOString().slice(0, 16) : "",
      durationMinutes: cls.durationMinutes,
      meetingUrl: cls.meetingUrl ?? "",
      topicId: cls.topicId,
      quizQuestionCount: cls.quizQuestionCount,
      recordingUrl: cls.recordingUrl ?? "",
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Live Classes</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage summer live class schedule, registrations, and post-class quizzes.</p>
      </div>

      {/* Feature flag + tunables */}
      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="summer-toggle" className="font-medium">Enable Summer Live Classes</Label>
              <p className="text-xs text-muted-foreground mt-1">When off, the Classes tab is hidden from students and all class endpoints return 404.</p>
            </div>
            <Switch
              id="summer-toggle"
              checked={status.runtime}
              onCheckedChange={(v) => setRuntimeFlag.mutate(v)}
              data-testid="toggle-summer-classes"
            />
          </div>
          <div className="flex items-center gap-3 max-w-sm">
            <Label htmlFor="quiz-len" className="font-medium whitespace-nowrap">Default post-class quiz length</Label>
            <Input
              id="quiz-len"
              type="number"
              min={5}
              max={100}
              defaultValue={status.defaultQuizLength}
              onBlur={(e) => {
                const n = parseInt(e.target.value, 10);
                if (n && n !== status.defaultQuizLength) setDefaultQuizLength.mutate(n);
              }}
              className="w-24"
            />
            <span className="text-xs text-muted-foreground">questions</span>
          </div>
        </CardContent>
      </Card>

      {/* Filters + create */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Label className="text-sm">Grade:</Label>
          {["all", "4", "5", "6", "7"].map((g) => (
            <Button
              key={g}
              size="sm"
              variant={gradeFilter === g ? "default" : "outline"}
              onClick={() => setGradeFilter(g)}
            >
              {g === "all" ? "All" : `Grade ${g}`}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => seedDemo.mutate()} disabled={seedDemo.isPending}>
            <FlaskConical className="h-4 w-4 mr-2" /> Seed demo
          </Button>
          <Button onClick={() => { setCreating(true); setForm(EMPTY_CLASS); }}>
            <Plus className="h-4 w-4 mr-2" /> New Class
          </Button>
        </div>
      </div>

      {/* Classes list */}
      {classesLoading ? (
        <div>Loading classes…</div>
      ) : classes.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">No classes scheduled yet. Click "New Class" to create one.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {classes.map((cls) => (
            <Card key={cls.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline">Grade {cls.grade}</Badge>
                      <Badge variant={cls.status === "upcoming" ? "default" : cls.status === "ended" ? "secondary" : "destructive"}>
                        {cls.status}
                      </Badge>
                      {cls.subject && <Badge variant="outline">{cls.subject}</Badge>}
                    </div>
                    <h3 className="font-semibold">{cls.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {cls.teacherName} · {formatLocal(cls.scheduledAt)} · {cls.durationMinutes} min
                    </p>
                    {cls.meetingUrl && <p className="text-xs text-muted-foreground mt-1 truncate">Meeting: {cls.meetingUrl}</p>}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => start(cls)}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    {cls.status !== "ended" && cls.status !== "cancelled" && (
                      <Button size="sm" variant="secondary" onClick={() => endClass.mutate(cls.id)}>
                        <Flag className="h-3 w-3 mr-1" /> End + Assign Quiz
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => {
                      if (confirm(`Delete "${cls.title}"? This removes registrations and quiz assignments too.`)) deleteClass.mutate(cls.id);
                    }}>
                      <Trash2 className="h-3 w-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog (shared form) */}
      <Dialog open={creating || !!editing} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Class" : "New Class"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label>Grade</Label>
              <select className="w-full border rounded px-3 py-2 text-sm" value={form.grade} onChange={(e) => setForm({ ...form, grade: parseInt(e.target.value, 10) })}>
                {[4, 5, 6, 7].map((g) => <option key={g} value={g}>Grade {g}</option>)}
              </select>
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={form.subject || ""} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Mathematics" />
            </div>
            <div className="col-span-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Fractions Made Easy" />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>Teacher Name</Label>
              <Input value={form.teacherName} onChange={(e) => setForm({ ...form, teacherName: e.target.value })} />
            </div>
            <div>
              <Label>Date & Time (Jamaica)</Label>
              <Input type="datetime-local" value={form.scheduledAt} onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })} />
            </div>
            <div>
              <Label>Duration (min)</Label>
              <Input type="number" value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: parseInt(e.target.value, 10) || 60 })} />
            </div>
            <div>
              <Label>Quiz length override</Label>
              <Input type="number" placeholder={`default ${status.defaultQuizLength}`} value={form.quizQuestionCount ?? ""} onChange={(e) => setForm({ ...form, quizQuestionCount: e.target.value ? parseInt(e.target.value, 10) : null })} />
            </div>
            <div className="col-span-2">
              <Label>Meeting URL (Google Meet, Zoom, etc.)</Label>
              <Input value={form.meetingUrl || ""} onChange={(e) => setForm({ ...form, meetingUrl: e.target.value })} placeholder="https://meet.google.com/..." />
            </div>
            <div className="col-span-2">
              <Label>Recording URL (post-class)</Label>
              <Input value={form.recordingUrl || ""} onChange={(e) => setForm({ ...form, recordingUrl: e.target.value })} placeholder="Paste after class is recorded" />
            </div>
            <div className="col-span-2">
              <Label>Topic ID (for post-class quiz)</Label>
              <Input value={form.topicId || ""} onChange={(e) => setForm({ ...form, topicId: e.target.value || null })} placeholder="Topic UUID from /admin/curriculum" />
              <p className="text-xs text-muted-foreground mt-1">When admin marks class ended, questions from this topic auto-assign to all registrants.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button>
            <Button
              onClick={() => {
                if (!form.title || !form.teacherName || !form.scheduledAt) {
                  toast({ title: "Missing fields", description: "Title, teacher, and start time are required.", variant: "destructive" });
                  return;
                }
                if (editing) updateClass.mutate({ id: editing.id, payload: form });
                else createClass.mutate(form);
              }}
              disabled={createClass.isPending || updateClass.isPending}
            >
              {editing ? "Save changes" : "Create class"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
